import { Flex, Button, Box } from "@chakra-ui/react";
import { AppDispatch } from "@root/store";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { updateLanguage as updateLanguageThunk } from "@root/store/thunks";
import { updateLanguage as updateLanguageAction } from "@root/store/slices/features/auth";
import { useEffect, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GlobeIcon } from "@root/assets/icons/globe-icon";
import { LANGUAGE } from "@root/constants";
import { GlobalContext } from "@root/context/global-context";

interface LanguageToggleProps {
  languages?: Array<{ title: string; value: string; onClick?: () => void; isActive?: boolean }>;
  isSidebarOpen?: boolean;
  variant?: "sidebar" | "public";
  position?: "absolute" | "relative" | "fixed";
  top?: string;
  right?: string;
  zIndex?: number;
}

export const LanguageToggle = ({
  languages,
  isSidebarOpen,
  variant = "sidebar",
  position = "relative",
  top,
  right,
  zIndex = 1000
}: LanguageToggleProps) => {
  const { i18n, t } = useTranslation("common");
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Use context directly to avoid error on public routes where GlobalContextProvider may not exist
  const globalContextValue = useContext(GlobalContext);
  const setIsSidebarOpen = globalContextValue?.setIsSidebarOpen || (() => { });

  // Default languages if not provided
  const defaultLanguages = [
    {
      title: "ENG",
      value: "en",
      onClick: () => { },
      isActive: i18n.language === "en",
    },
    {
      title: "KOR",
      value: "ko",
      onClick: () => { },
      isActive: i18n.language === "ko",
    },
  ];

  const languagesToUse = languages || defaultLanguages;

  useEffect(() => {
    if (isLoggedInUser) {
      const backendLang = isLoggedInUser.language;
      const savedLang = localStorage.getItem("appLanguage");
      const currentLang = i18n.language;

      // Priority: localStorage (user's current selection) > backend preference
      // If user selected language on public routes, use that and sync to backend
      if (savedLang) {
        const lang = savedLang.toLowerCase();
        // Only update if current language differs from saved language
        if (currentLang !== lang) {
          i18n.changeLanguage(lang);
        }
        // Sync to backend if it differs from backend preference
        if (!backendLang || backendLang.toLowerCase() !== lang) {
          dispatch(
            updateLanguageThunk({
              language: lang as "en" | "ko",
            })
          );
        }
      }
      // If no localStorage language but backend has one, use backend language
      else if (backendLang) {
        const lang = backendLang.toLowerCase();
        // Only update if current language differs from backend language
        if (currentLang !== lang) {
          i18n.changeLanguage(lang);
          localStorage.setItem("appLanguage", lang);
        }
      }
    }
  }, [isLoggedInUser, dispatch, i18n]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageMenu]);

  const handleLanguageChange = (langValue: string) => {
    i18n.changeLanguage(langValue);
    localStorage.setItem("appLanguage", langValue);

    // Update Redux state immediately so API headers reflect the new language
    dispatch(updateLanguageAction(langValue.toUpperCase()));

    // Only sync with backend if user is logged in (for sidebar variant)
    if (isLoggedInUser && variant === "sidebar") {
      dispatch(
        updateLanguageThunk({
          language: langValue as "en" | "ko",
        })
      );
    }
    setShowLanguageMenu(false);
  };

  // Map language values to full names
  const getLanguageFullName = (value: string) => {
    const key = value === "en" ? "language.english" : "language.korean";
    return t(key)?.toUpperCase() || value?.toUpperCase();
  };

  // Public route variant - simple button style
  if (variant === "public") {
    return (
      <Flex
        ref={menuRef}
        position={position}
        top={top}
        right={right}
        zIndex={zIndex}
        userSelect="none"
      >
        <Flex
          alignItems="center"
          bg="#E5E7EB"
          borderRadius="24px"
          fontWeight="600"
          fontSize="12px"
          p="2px"
          h="36px"
          width="200px"
          justifyContent="center"
          gap="0"
        >
          {languagesToUse.map((lang: any) => (
            <Button
              key={lang?.value}
              onClick={() => handleLanguageChange(lang?.value)}
              h="32px"
              fontSize="12px"
              fontWeight="600"
              variant="ghost"
              px="6px"
              minW="auto"
              bg={i18n.language === lang?.value ? "white" : "transparent"}
              borderRadius="20px"
              color={i18n.language === lang?.value ? "#14B8A6" : "#6B7280"}
              textTransform="uppercase"
              _hover={{
                bg: i18n.language === lang?.value ? "white" : "transparent",
              }}
              _active={{
                bg: i18n.language === lang?.value ? "white" : "transparent",
              }}
              transition="all 0.2s ease"
              boxShadow={
                i18n.language === lang?.value
                  ? "0 1px 3px rgba(0, 0, 0, 0.1)"
                  : "none"
              }
            >
              {getLanguageFullName(lang?.value)}
            </Button>
          ))}
        </Flex>
      </Flex>
    );
  }

  // Sidebar variant - existing behavior
  // When sidebar is closed, show globe icon
  if (!isSidebarOpen) {
    return (
      <Flex
        ref={menuRef}
        flexDir="column"
        userSelect="none"
        position="relative"
        mt="10px"
        mb="10px"
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          width="40px"
          height="40px"
          borderRadius="50%"
          cursor="pointer"
          backgroundColor={"white"}
          border={"1px solid #E5E7EB"}
        >
          <Box color="#0FABBE" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <GlobeIcon />
          </Box>
        </Flex>
      </Flex>
    );
  }

  // When sidebar is open, show full language toggle
  return (
    <Flex
      alignItems="center"
      bg="#E5E7EB"
      borderRadius="24px"
      fontWeight="600"
      fontSize="12px"
      p="2px"
      h="36px"
      width="fit-content"
      maxW="200px"
      mb="5"
      justifyContent="center"
      gap="0"
    >
      {languagesToUse.map((lang: any) => (
        <Button
          key={lang?.value}
          onClick={() => handleLanguageChange(lang?.value)}
          flex="1"
          h="32px"
          fontSize="12px"
          fontWeight="600"
          variant="ghost"
          px="16px"
          bg={i18n.language === lang?.value ? "white" : "transparent"}
          borderRadius="20px"
          color={i18n.language === lang?.value ? "#14B8A6" : "#6B7280"}
          textTransform="uppercase"
          _hover={{
            bg: i18n.language === lang?.value ? "white" : "transparent",
          }}
          _active={{
            bg: i18n.language === lang?.value ? "white" : "transparent",
          }}
          transition="all 0.2s ease"
          boxShadow={
            i18n.language === lang?.value
              ? "0 1px 3px rgba(0, 0, 0, 0.1)"
              : "none"
          }
        >
          {getLanguageFullName(lang?.value)}
        </Button>
      ))}
    </Flex>
  );
};
